/**
 * Consulta de CEP no ViaCEP.
 *
 * A chamada vai direta do navegador, sem o interceptor do `api`: o ViaCEP é
 * serviço externo e não usa o JWT do SigaPsi — passar por lá faria um 404 de CEP
 * cair no tratamento de erro da API (inclusive no logout automático de 401).
 *
 * O ViaCEP responde 200 com `{ erro: true }` quando o CEP não existe, então o
 * status HTTP não serve para decidir sozinho.
 */

const TIMEOUT_MS = 8000;

/** Normaliza para os 8 dígitos que o ViaCEP espera. */
export const cepDigits = (valor) => (valor || '').replace(/\D/g, '').slice(0, 8);

export const cepCompleto = (valor) => cepDigits(valor).length === 8;

/**
 * Busca o endereço de um CEP.
 *
 * @param {string} cep CEP com ou sem máscara.
 * @returns {Promise<{ok: boolean, motivo?: 'incompleto'|'nao_encontrado'|'falha', endereco?: object}>}
 *          `ok: false` nunca lança: a tela decide a mensagem pelo `motivo`.
 */
export const buscarCep = async (cep) => {
    const digitos = cepDigits(cep);
    if (digitos.length !== 8) return { ok: false, motivo: 'incompleto' };

    // AbortController em vez de esperar o timeout do navegador: sem isso o
    // formulário fica com o campo travado em "buscando" por tempo indefinido.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`, {
            signal: controller.signal,
        });
        if (!resposta.ok) return { ok: false, motivo: 'falha' };
        const dados = await resposta.json();
        if (dados?.erro) return { ok: false, motivo: 'nao_encontrado' };
        return {
            ok: true,
            endereco: {
                cep: digitos,
                logradouro: dados.logradouro || '',
                complemento: dados.complemento || '',
                bairro: dados.bairro || '',
                // `localidade`/`uf` são os nomes do ViaCEP; o back espera
                // nomeCidade/ufEstado, e a conversão fica na tela que grava.
                localidade: dados.localidade || '',
                uf: dados.uf || '',
            },
        };
    } catch {
        return { ok: false, motivo: 'falha' };
    } finally {
        clearTimeout(timer);
    }
};

/** Mensagem pronta para toast, por motivo de falha. */
export const mensagemFalhaCep = (motivo) => {
    switch (motivo) {
        case 'nao_encontrado': return 'CEP não encontrado.';
        case 'incompleto': return 'Informe os 8 dígitos do CEP.';
        default: return 'Não foi possível consultar o ViaCEP. Preencha o endereço manualmente.';
    }
};
